import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LinhaTabela } from "shared/components/table/LinhaTabela";
import { RegistroTabela } from "shared/components/table/RegistroTabela";
import CustomButton from "shared/components/button/CustomButton";
import { Rotas } from "constants/EnumRotas";
import { BsChevronRight, BsShieldFillCheck } from "react-icons/bs";
import CustomSwitch from "shared/components/Switch/CustomSwitch";
import { usePutAtualizarConfigAssDigitalCondominio } from "hooks/groupsign/usePutAtualizarConfigAssDigitalCondominio";
import { ConfigAssDigitalCondominioDTO } from "types/dto/groupsign/ConfigAssDigitalCondominioDTO";
import { Grid } from "@mui/material";
import { formatCNPJ } from "shared/util/formatters/cnpj";
import CustomAlert from "shared/components/customAlert/CustomAlert";
import { toast } from "react-hot-toast";
import "./hyperLinkGroupSign.css";
import { usePermissoesContext } from "context/permissoes/PermissoesContext";
import { TipoPermissao } from "constants/EnumTipoPermissao";


interface RenderRowsProps {
  configsAssDigital: ConfigAssDigitalCondominioDTO[];
  refetch: () => void;
}

export const RenderRowsConfiguracaoCondominio: React.FC<RenderRowsProps> = ({
  configsAssDigital: initialConfigs,
  refetch,
}) => {
  const navigate = useNavigate();
  const { usuarioPossuiPermissao } = usePermissoesContext();
    const possuiPermissaoIncluirConfiguracaoCondominio = usuarioPossuiPermissao(
      "configuracaoCondominio",
      TipoPermissao.INCLUIR
  );
  const possuiPermissaoStatusAssinatura = usuarioPossuiPermissao(
      "statusAssinatura",
      TipoPermissao.EXECUTAR
  );
  const [showAlert, setShowAlert] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ConfigAssDigitalCondominioDTO | null>(null);
  const [newStatus, setNewStatus] = useState<boolean | null>(null);
  const [configsAssDigital, setConfigsAssDigital] = useState(initialConfigs);
  const { mutate: updateConfigAssDigital } = usePutAtualizarConfigAssDigitalCondominio();

  useEffect(() => {
    setConfigsAssDigital(initialConfigs);
  }, [initialConfigs]);

  const handleNavigation = (configAssDigitalUuid: string) => {
    navigate(Rotas.ASSINATURA_DIGITAL_CONFIGURACAO_CONDOMINIO, { state: configAssDigitalUuid });
  };

  const toggleStatus = (config: ConfigAssDigitalCondominioDTO) => {
    setSelectedConfig(config);
    setNewStatus(!config.isAtivo);
    setShowAlert(true);
  };

  const closeAlert = () => {
    setShowAlert(false);
    setSelectedConfig(null);
    setNewStatus(null);
  };

  const actionText = newStatus ? "ativar" : "desativar";

  const subMessage =
    newStatus
      ? "Essa ação tornará a funcionalidade disponível para os usuários."
      : "Essa ação tornará a funcionalidade indisponível para os usuários.";

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const confirmAlert = () => {
    if (!selectedConfig || newStatus === null) return;

    const payload = { ...selectedConfig, isAtivo: newStatus };
    updateConfigAssDigital(payload, {
      onSuccess: () => {
        setShowAlert(false);
        setSelectedConfig(null);
        setNewStatus(null);
        toast.success(
          `Assinatura digital do condomínio ${actionText === "ativar" ? "ativada" : "desativada"} com sucesso.`
        );
        refetch();
      },
    });
  };

  return (
    <>
      <CustomAlert
        icon={BsShieldFillCheck}
        iconColor="black"
        open={showAlert}
        onClose={closeAlert}
        height={"60vh"}
        width={"45vw"}
        title={`${capitalizeFirstLetter(actionText)} a assinatura digital no condomínio`}
        message={`Deseja ${actionText} a assinatura digital no condomínio ${selectedConfig?.nomeCondominio}?`}
        subMessage={subMessage}
        messageAlignment="left"
        buttons={
          <>
            <CustomButton
              id="cancelar-config-ass-digital-condominio"
              label="Cancelar"
              onClick={closeAlert}
              size="lg"
              className="mb-3 me-3 "
              style={{ width: 150 }}
              variant="outline-primary"
            />
            <CustomButton
              id="confirmar-config-ass-digital-condominio"
              label={`${capitalizeFirstLetter(actionText)} assinatura`}
              onClick={confirmAlert}
              size="lg"
              className="mb-3"
              style={{ width: 200 }}
              variant="primary"
            />
          </>
        }
      />

      {configsAssDigital.map((config) => (
        <LinhaTabela key={config.id} id={`linha-${config.id}`}>
          <RegistroTabela id={`nome-${config.id}`}>{config.nomeCondominio}</RegistroTabela>
          <RegistroTabela id={`cnpj-${config.id}`}>{config.cnpj ? formatCNPJ(config.cnpj) : "-"}</RegistroTabela>
          <RegistroTabela id={`status-${config.id}`}>
          {possuiPermissaoStatusAssinatura && (
            <CustomSwitch
              labelDireito={config?.isAtivo ? "Ativada" : "Desativada"}
              checked={!!config?.isAtivo}
              onChange={() => toggleStatus(config)}
              usarValidacaoParaFormulario={false}
            />
          )}
          </RegistroTabela>
          <RegistroTabela id={`btn-${config.id}`}>
            <Grid display="flex" justifyContent="center">
              {possuiPermissaoIncluirConfiguracaoCondominio && (
                <span
                  className={`hyperlink-${config.isAtivo ? "ativo" : "inativo"}`}
                  style={{ fontSize: 12, fontWeight: "bold", marginLeft: 15, display: "flex", alignItems: "center" }}
                  onClick={() => config.isAtivo && handleNavigation(config.uuid)}
                >
                  Ver detalhes {">"}
                </span>
              )}
            </Grid>
          </RegistroTabela>
        </LinhaTabela>
      ))}
    </>
  );
};
